import { Text } from '@chakra-ui/react';

import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { DeductionAdjustment } from 'app/types/deductionAdjustments';

import { formatDateTime } from 'app/utils/reconciliations';
import { formatCurrency } from 'app/utils/strings';

/**
 * @description The amount as it counts on the report: positive to deduct, negative for a net addition.
 */
export const signedAdjustmentAmount = ({ direction, amount }: DeductionAdjustment) =>
  direction === 'NET_ADDITION' ? -amount : amount;

/**
 * @description Whether the adjustment was added automatically to move a deduction between pay calendars.
 */
export const isPayCalendarMove = ({ source }: DeductionAdjustment) =>
  source === 'PAY_CALENDAR_MOVE';

/**
 * @description Columns of a list of deduction adjustments: when, what kind, who, which way, how much and why.
 */
function getDeductionAdjustmentColumns() {
  const columnHelper = createColumnHelper<DeductionAdjustment>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<DeductionAdjustment, any>[] = [
    columnHelper.accessor('createdAt', {
      header: 'Added',
      enableSorting: false,
      cell: (info) => formatDateTime(info.getValue()),
    }),
    columnHelper.display({
      id: 'type',
      header: 'Type',
      cell: (info) => (isPayCalendarMove(info.row.original) ? 'Pay calendar move' : 'Manual'),
    }),
    columnHelper.accessor('createdByName', {
      header: 'Added by',
      enableSorting: false,
      cell: (info) => {
        if (isPayCalendarMove(info.row.original)) return 'Automatic';
        return info.getValue() || info.row.original.createdBy;
      },
    }),
    columnHelper.accessor('direction', {
      header: 'Adjustment',
      enableSorting: false,
      cell: (info) => (info.getValue() === 'NET_ADDITION' ? 'Net addition' : 'Deduct more'),
    }),
    columnHelper.display({
      id: 'amount',
      header: 'Amount',
      cell: (info) =>
        formatCurrency(signedAdjustmentAmount(info.row.original), info.row.original.currency),
    }),
    columnHelper.accessor('notes', {
      header: 'Reason',
      enableSorting: false,
      cell: (info) => (
        <Text maxW="420px" whiteSpace="normal">
          {info.getValue()}
        </Text>
      ),
    }),
  ];

  return columns;
}

export default getDeductionAdjustmentColumns;
